import { useState } from 'react'
import { 
  ChevronRightIcon, 
  ChevronDownIcon,
  DocumentArrowDownIcon,
  LinkIcon,
  FolderIcon,
  FolderOpenIcon
} from '@heroicons/react/24/outline'

export default function ResourceTree({ resources }) {
  return (
    <div className="space-y-1">
      {resources.map((resource, idx) => (
        <ResourceNode key={idx} resource={resource} level={0} />
      ))}
    </div>
  )
}

function ResourceNode({ resource, level }) {
  const [isOpen, setIsOpen] = useState(level === 0) // Top level folders open by default

  const hasChildren = resource.children && resource.children.length > 0
  const isFolder = resource.type === 'folder'
  const isDownload = resource.type === 'download'
  const isLink = resource.type === 'link'

  const paddingLeft = `${level * 1.25}rem`

  if (isFolder) {
    return (
      <div>
        {/* Folder Header */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 rounded-lg transition-colors group"
          style={{ paddingLeft }}
        >
          {isOpen ? (
            <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
          )}
          {isOpen ? (
            <FolderOpenIcon className="h-5 w-5 text-purple-500 flex-shrink-0" />
          ) : (
            <FolderIcon className="h-5 w-5 text-purple-500 flex-shrink-0" />
          )}
          <span className="font-medium text-gray-700 group-hover:text-gray-900">
            {resource.title}
          </span>
        </button>

        {/* Folder Children */}
        {isOpen && hasChildren && (
          <div className="mt-1">
            {resource.children.map((child, idx) => (
              <ResourceNode key={idx} resource={child} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    )
  }

  if (isDownload) {
    return (
      <a
        href={resource.url}
        download
        className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-purple-50 rounded-lg transition-colors group"
        style={{ paddingLeft }}
      >
        <DocumentArrowDownIcon className="h-4 w-4 text-purple-600 flex-shrink-0" />
        <span className="text-gray-700 group-hover:text-purple-700 flex-1 min-w-0 truncate">
          {resource.title}
        </span>
        <svg className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
        </svg>
      </a>
    )
  }

  if (isLink) {
    return (
      <a
        href={resource.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-blue-50 rounded-lg transition-colors group"
        style={{ paddingLeft }}
      >
        <LinkIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
        <span className="text-gray-700 group-hover:text-blue-700 flex-1 min-w-0 truncate">
          {resource.title}
        </span>
        <svg className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </a>
    )
  }

  // Fallback for unknown types
  return (
    <div 
      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700"
      style={{ paddingLeft }}
    >
      <span>{resource.title}</span>
    </div>
  )
}
